import { useEffect, useRef, useState } from "react";
import type { ProjectSummary } from "../lib/api.ts";
import { t } from "../lib/i18n.ts";
import { Spinner, fmtDate, useApp } from "./common.tsx";
import { cachedThumb, renderThumb } from "./templates.tsx";

const kindOf = (p: ProjectSummary) => (!p.width || !p.height ? "h" : p.height > p.width ? "v" : p.height === p.width ? "s" : "h");
const tag = (k: string) => (k === "v" ? "9:16" : k === "s" ? "1:1" : "16:9");

function useSeen() {
  const ref = useRef(null as HTMLElement | null); const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setSeen(true); return; }
    const io = new IntersectionObserver((e) => { if (e.some((y) => y.isIntersecting)) { setSeen(true); io.disconnect(); } }, { rootMargin: "300px" });
    io.observe(el); return () => io.disconnect();
  }, []);
  return [ref, seen] as const;
}

function Pin({ p, onDelete, onDuplicate }: { key?: string; p: ProjectSummary; onDelete?: (p: ProjectSummary) => void; onDuplicate?: (p: ProjectSummary) => void }) {
  const { api, go } = useApp();
  const [ref, seen] = useSeen();
  const [src, setSrc] = useState(cachedThumb(p.id, p.revision)); const [failed, setFailed] = useState(false); const [menu, setMenu] = useState(false);
  useEffect(() => {
    if (!seen || src || failed) return;
    let live = true;
    api.getProject(p.id).then((full) => renderThumb(full)).then((u) => { if (live) setSrc(u); }, () => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [seen, p.id, p.revision]);
  useEffect(() => {
    if (!menu) return;
    const close = (e: any) => { if (!e.target.closest?.(".pin-menu, .pin-more")) setMenu(false); };
    addEventListener("pointerdown", close); return () => removeEventListener("pointerdown", close);
  }, [menu]);
  const k = kindOf(p), name = p.name || t("ed.untitled"), open = () => go(`/editor/${p.id}`);
  return (
    <article className="pin" ref={ref as any}>
      <div className="pin-img" style={{ aspectRatio: p.width && p.height ? `${p.width} / ${p.height}` : "9 / 16" }} role="link" tabIndex={0} aria-label={`${t("projects.open")} ${name}`}
        onClick={(e: any) => { if (!e.target.closest?.(".pin-more, .pin-menu")) open(); }} onKeyDown={(e: any) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); open(); } }}>
        {src ? <img src={src} alt="" className="on" draggable={false} /> : failed ? <span className="pin-ph">{tag(k)}</span> : <span className="pin-ph"><Spinner /></span>}
        <span className="pin-tag">{tag(k)}{p.duration_ms ? ` · ${Math.round(p.duration_ms / 1000)} s` : ""}</span>
        {(onDelete || onDuplicate) && <>
          <button className="pin-more" aria-label={t("pin.more")} aria-expanded={menu} aria-haspopup="menu" onClick={(e: any) => { e.stopPropagation(); setMenu(!menu); }}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg></button>
          {menu && <div className="pin-menu" role="menu">
            <button role="menuitem" onClick={() => { setMenu(false); open(); }}>{t("projects.open")}</button>
            {onDuplicate && <button role="menuitem" onClick={() => { setMenu(false); onDuplicate(p); }}>{t("pin.duplicate")}</button>}
            {onDelete && <button role="menuitem" className="danger" onClick={() => { setMenu(false); onDelete(p); }}>{t("projects.delete")}</button>}
          </div>}</>}
        <div className="pin-over"><strong>{name}</strong>{p.scene_count ? <p>{t("pin.scenes", { n: p.scene_count })}</p> : null}<span className="btn primary sm">{t("projects.open")}</span></div>
      </div>
      <div className="pin-meta"><strong title={name}>{name}</strong><span className="muted">{t("projects.updated", { date: fmtDate(p.updated_at) })}</span></div>
    </article>
  );
}

export function Board({ items, onDelete, onDuplicate }: { items: ProjectSummary[]; onDelete?: (p: ProjectSummary) => void; onDuplicate?: (p: ProjectSummary) => void }) {
  return <div className="board">{items.map((p) => <Pin key={p.id} p={p} onDelete={onDelete} onDuplicate={onDuplicate} />)}</div>;
}
